import api from './api';

class HeartbeatService {
  private static instance: HeartbeatService;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private isActive = false;

  constructor() {
    // Make sure we have a singleton
    if (HeartbeatService.instance) {
      return HeartbeatService.instance;
    }
    HeartbeatService.instance = this;
  }

  /**
   * Start automatic heartbeat every 30 minutes
   * Only works when user is logged in and page is visible
   */
  startHeartbeat(): void {
    if (this.isActive) {
      return; // Already running
    }

    this.isActive = true;
    console.log('🔄 Starting heartbeat service (30-minute intervals)');

    // Send initial heartbeat
    this.sendHeartbeat();

    // Set up interval for every 30 minutes (30 * 60 * 1000 = 1,800,000 ms)
    this.heartbeatInterval = setInterval(() => {
      // Only send heartbeat if page is visible and user is logged in
      if (!document.hidden && this.isUserLoggedIn()) {
        this.sendHeartbeat();
      }
    }, 30 * 60 * 1000); // 30 minutes

    // Listen for page visibility changes
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
  }

  /**
   * Stop the heartbeat service
   */
  stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    
    this.isActive = false;
    document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    console.log('🔄 Heartbeat service stopped');
  }

  /**
   * Send heartbeat to server
   */
  private async sendHeartbeat(): Promise<void> {
    try {
      // Check if user is authenticated before sending heartbeat
      if (!this.isUserLoggedIn()) {
        console.log('💓 Heartbeat skipped - user not logged in');
        return;
      }

      // Check if we're on a public route (additional safety check)
      if (this.isOnPublicRoute()) {
        console.log('💓 Heartbeat skipped - on public route');
        this.stopHeartbeat();
        return;
      }

      console.log('💓 Sending heartbeat request...');
      const response = await api.get('/user/heartbeat');
      console.log('💓 Heartbeat sent successfully:', response.data);
    } catch (error: any) {
      // Handle authentication errors gracefully
      if (error.response?.status === 401) {
        console.log('💓 Heartbeat skipped - authentication required');
        this.stopHeartbeat();
        return;
      }
      
      // Log more details for debugging
      console.error('💓 Heartbeat failed:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
    }
  }

  /**
   * Handle page visibility changes
   */
  private handleVisibilityChange(): void {
    if (!document.hidden && this.isUserLoggedIn()) {
      // Page became visible - send heartbeat immediately
      this.sendHeartbeat();
    }
  }

  /**
   * Check if user is logged in
   */
  private isUserLoggedIn(): boolean {
    const token = localStorage.getItem('auth_token');
    return !!token;
  }

  /**
   * Check if current route is a public route
   */
  private isOnPublicRoute(): boolean {
    const currentPath = window.location.pathname;
    const publicRoutes = [
      '/',
      '/features',
      '/pricing',
      '/about',
      '/contact',
      '/privacy',
      '/terms',
      '/login',
      '/signup',
      '/forgot-password',
      '/public/checkin',
      '/public/checkin/verify',
      '/public/checkin/review',
      '/public/checkin/token',
      '/admin/lobby-display',
      '/resume-viewer'
    ];
    
    return publicRoutes.some(route => currentPath.startsWith(route));
  }

  /**
   * Get heartbeat status
   */
  getStatus(): { isActive: boolean; interval: number } {
    return {
      isActive: this.isActive,
      interval: 30 * 60 * 1000 // 30 minutes in milliseconds
    };
  }
}

// Export singleton instance
export const heartbeatService = new HeartbeatService();
export default heartbeatService;
